import React from 'react';

import Icon from '@/components/ui/AppIcon';

interface PlantZone {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  plantType: string;
  color: string;
}

interface PlanVisualizationProps {
  spaceData: {
    width: number;
    height: number;
    spaceType: string;
  };
  plantZones: PlantZone[];
}

const PlanVisualization = ({ spaceData, plantZones }: PlanVisualizationProps) => {
  const scaleX = 400 / spaceData.width;
  const scaleY = 300 / spaceData.height;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Space Layout Visualization</h3>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="MapIcon" size={16} />
          <span>{spaceData.width}' × {spaceData.height}'</span>
        </div>
      </div>

      <div className="relative bg-surface rounded-lg border-2 border-dashed border-border overflow-hidden">
        <svg
          width="100%"
          height="300"
          viewBox="0 0 400 300"
          className="bg-gradient-to-br from-green-50 to-green-100"
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Plant zones */}
          {plantZones.map((zone) => (
            <g key={zone.id}>
              <rect
                x={zone.x * scaleX}
                y={zone.y * scaleY}
                width={zone.width * scaleX}
                height={zone.height * scaleY}
                fill={zone.color}
                fillOpacity="0.3"
                stroke={zone.color}
                strokeWidth="2"
                rx="4"
              />
              <text
                x={zone.x * scaleX + (zone.width * scaleX) / 2}
                y={zone.y * scaleY + (zone.height * scaleY) / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-medium fill-current text-foreground"
              >
                {zone.name}
              </text>
            </g>
          ))}

          {/* Compass */}
          <g transform="translate(360, 40)">
            <circle cx="0" cy="0" r="15" fill="white" stroke="#d1d5db" strokeWidth="1"/>
            <text x="0" y="-8" textAnchor="middle" className="text-xs font-bold fill-current text-foreground">N</text>
            <polygon points="0,-10 3,0 0,10 -3,0" fill="#ef4444"/>
          </g>
        </svg>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {plantZones.map((zone) => (
          <div key={zone.id} className="flex items-center space-x-2 text-sm">
            <div
              className="w-3 h-3 rounded-sm border"
              style={{ backgroundColor: zone.color }}
            />
            <span className="text-foreground font-medium">{zone.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanVisualization;